import { HeightField } from '../terrain/HeightField';
import type { Vec2 } from '../utils/geometry';

export type TerrainBrushTool =
  'raise' | 'lower' | 'smooth' | 'flatten' | 'slope' | 'greenContour' | 'bunkerRecess';
export type TerrainBrushFalloff = 'linear' | 'smooth' | 'constant';

export interface TerrainBrushSettings {
  radius: number;
  strength: number;
  falloff: TerrainBrushFalloff;
  targetHeight?: number;
  slopeDirection?: Vec2;
}

export interface TerrainHeightMapDocument {
  width: number;
  depth: number;
  centerX: number;
  centerZ: number;
  resolution: number;
  samples: number[];
}

export const DEFAULT_TERRAIN_RESOLUTION = 96;
export const DEFAULT_GREEN_MAX_SLOPE_PERCENT = 5;

export function emptyTerrainHeightMap(
  width = 520,
  depth = 720,
  resolution = DEFAULT_TERRAIN_RESOLUTION,
): TerrainHeightMapDocument {
  return {
    width,
    depth,
    centerX: 0,
    centerZ: 180,
    resolution,
    samples: new Array((resolution + 1) * (resolution + 1)).fill(0),
  };
}

export function heightFieldFromDocument(doc: TerrainHeightMapDocument): HeightField {
  const field = new HeightField({
    width: doc.width,
    depth: doc.depth,
    centerX: doc.centerX,
    centerZ: doc.centerZ,
    resolution: doc.resolution,
  });
  field.gridData.set(doc.samples.slice(0, field.gridData.length));
  return field;
}

export function documentFromHeightField(field: HeightField): TerrainHeightMapDocument {
  return {
    width: field.width,
    depth: field.depth,
    centerX: field.centerX,
    centerZ: field.centerZ,
    resolution: field.resolution,
    samples: Array.from(field.gridData, (n) => Number(n.toFixed(4))),
  };
}

export function applyTerrainBrush(
  field: HeightField,
  center: Vec2,
  tool: TerrainBrushTool,
  settings: TerrainBrushSettings,
): void {
  const radius = Math.max(0.1, settings.radius);
  const strength = settings.strength;
  const before = new Float32Array(field.gridData);
  forEachBrushSample(field, center, radius, settings.falloff, (index, ix, iz, weight, x, z) => {
    const current = before[index];
    let next = current;
    if (tool === 'raise') next = current + strength * weight;
    else if (tool === 'lower') next = current - strength * weight;
    else if (tool === 'flatten')
      next =
        current +
        ((settings.targetHeight ?? field.sampleHeight(center.x, center.z)) - current) *
          Math.min(1, strength) *
          weight;
    else if (tool === 'smooth')
      next =
        current +
        (neighborAverage(field, before, ix, iz) - current) * Math.min(1, strength) * weight;
    else if (tool === 'slope') {
      const dir = normalize(settings.slopeDirection ?? { x: 1, z: 0 });
      const target =
        (settings.targetHeight ?? field.sampleHeight(center.x, center.z)) +
        ((x - center.x) * dir.x + (z - center.z) * dir.z) * strength;
      next = current + (target - current) * weight;
    } else if (tool === 'greenContour') {
      const contour =
        Math.sin(((x - center.x) / Math.max(1, radius)) * Math.PI * 2) *
        Math.cos(((z - center.z) / Math.max(1, radius)) * Math.PI * 2);
      next = current + contour * Math.min(Math.abs(strength), 0.35) * weight;
    } else if (tool === 'bunkerRecess') {
      next = current - Math.abs(strength) * weight * 0.85;
    }
    field.gridData[index] = next;
  });
}

function forEachBrushSample(
  field: HeightField,
  center: Vec2,
  radius: number,
  falloff: TerrainBrushFalloff,
  cb: (index: number, ix: number, iz: number, weight: number, x: number, z: number) => void,
): void {
  for (let iz = 0; iz < field.gridRows; iz++)
    for (let ix = 0; ix < field.gridCols; ix++) {
      const { x, z } = field.indexToWorld(ix, iz);
      const d = Math.hypot(x - center.x, z - center.z);
      if (d > radius) continue;
      const t = Math.max(0, 1 - d / radius);
      const weight = falloff === 'constant' ? 1 : falloff === 'smooth' ? t * t * (3 - 2 * t) : t;
      cb(iz * field.gridCols + ix, ix, iz, weight, x, z);
    }
}

function neighborAverage(field: HeightField, data: Float32Array, ix: number, iz: number): number {
  let total = 0,
    count = 0;
  for (let z = iz - 1; z <= iz + 1; z++)
    for (let x = ix - 1; x <= ix + 1; x++) {
      if (x < 0 || z < 0 || x >= field.gridCols || z >= field.gridRows) continue;
      total += data[z * field.gridCols + x];
      count++;
    }
  return total / Math.max(1, count);
}
function normalize(v: Vec2): Vec2 {
  const l = Math.hypot(v.x, v.z) || 1;
  return { x: v.x / l, z: v.z / l };
}

export function maxSlopePercent(field: HeightField, points: Vec2[]): number {
  let max = 0;
  for (const p of points) {
    const n = field.sampleNormal(p.x, p.z);
    max = Math.max(max, (Math.hypot(n.x, n.z) / Math.max(0.0001, n.y)) * 100);
  }
  return max;
}
